/**
 * El catálogo de mobiliario y sus símbolos de planta.
 *
 * Lo pidió Daniel el 4-oct-2026, y el argumento es el que lo hace viable: «este
 * tipo de casas son muy repetitivos los módulos de mobiliario». Las medidas de
 * esta tabla las dictó él una por una; las cuatro que dejó en «estándar» o sin
 * decir están marcadas abajo.
 *
 * ⚠️ Las medidas están en METROS REALES y entran tal cual, sin escalar por nada.
 * El editor entero trabaja en metros: la escala se resuelve UNA vez, en el
 * importador, leyendo los rótulos de cota del PDF (37,7953 pt/m = 1:75 en
 * Salamanca). Era la pregunta de Daniel —«no sé cómo manejas lo de la escala»— y
 * la respuesta es que de acá para adentro no hay puntos ni escala que manejar.
 *
 * Los símbolos se dibujan a mano en SVG y no con íconos: Daniel es arquitecto y
 * eligió «símbolos reales». Un rectángulo con la palabra «Cama» adentro no es un
 * plano. Además sale gratis en dependencias —no existen `Refrigerator`, `Washer`
 * ni `Sink` en Phosphor— y la misma función pinta la miniatura del catálogo, así
 * que lo que se elige es exactamente lo que se pone.
 */
import type { Mueble } from './tipos';
import type { Pared } from './tipos';
import type { Trazo } from './importar-simbolo';

/**
 * La tinta del mobiliario: un gris azulado de la paleta de materiales del
 * proyecto (el del faldón de lámina).
 *
 * Tiene que ser AJENO a los dos colores que ya hablan en este editor —el marrón
 * de las paredes y el verde de las áreas— porque el mueble no es ninguna de las
 * dos cosas: no mide y no es obra. Un mueble marrón se leería como tabique.
 */
export const TINTA_MUEBLE = '#5f6f80';

/**
 * La tinta del ELÉCTRICO, distinta de la del mobiliario por la misma razón por
 * la que el mobiliario es distinto de la pared: en este editor el color dice
 * QUÉ es cada cosa. Un tomacorriente del gris del sofá se leería como un mueble
 * chiquito, y lo que se conversa con el cliente es justamente «cuántas salidas
 * tiene este cuarto», no «qué mueble es ese».
 *
 * Es el marrón de las puertas de la paleta de materiales del proyecto —ajeno al
 * marrón de pared, que es más claro y relleno— y el único de la paleta que no
 * está hablando ya de otra cosa.
 */
export let TINTA_ELECTRICO = '#8a5a3b';

/**
 * Cambia la pluma eléctrica para TODO el grupo, de una.
 *
 * Es `let` exportado y no una constante a propósito: los módulos de ES exportan
 * **enlaces vivos**, así que los seis lugares que ya leen `TINTA_ELECTRICO`
 * —la vitrina, la miniatura, el rayo de la barra, el punto del resumen y el
 * exportador de símbolos— ven el valor nuevo sin que haya que tocarlos ni
 * pasarles el color por parámetro. Reescribir esos seis para recibir un prop
 * era el camino «correcto» y era peor: el color es una convención de dibujo del
 * equipo, no un estado de ningún componente.
 *
 * Lo llama `traerObjetos` al abrir y `guardarAjustes` al cambiarlo; quien
 * repinta es el `fijarCompartidos` que viene justo después.
 */
export function fijarTintaElectrico(hex: string) {
  // Solo se acepta `#rrggbb`: este valor termina crudo en el atributo `stroke`
  // del SVG que se descarga, y un nombre de color o un `rgb(...)` sin validar
  // haría un archivo que abre mal en AutoCAD sin avisar.
  if (/^#[0-9a-fA-F]{6}$/.test(hex)) TINTA_ELECTRICO = hex.toLowerCase();
}

/**
 * Lo que mide un símbolo eléctrico en el plano, en metros.
 *
 * NO es el tamaño del aparato: un tomacorriente real mide 7 cm y a 1:75 sería
 * un punto invisible. En un plano de verdad el símbolo eléctrico se dibuja a un
 * tamaño CONVENCIONAL, igual en toda la lámina, porque lo que comunica es «acá
 * hay una salida» y no «este objeto ocupa este espacio». 20 cm es lo que se lee
 * bien sin taparle el mobiliario al cuarto.
 */
export const TAM_ELECTRICO = 0.2;

/** El grosor de línea del contorno y del detalle, en metros. */
export const TRAZO = 0.022;
const FINO = 0.014;

/** El radio de la roseta de la regadera, en metros. Ver el símbolo `ducha`. */
const ROSETA = 0.075;

/**
 * Un símbolo se dibuja en coordenadas LOCALES —de (0,0) a (w,h)— y siempre con
 * su frente mirando al SUR (hacia la `y` que crece, que en pantalla es abajo).
 *
 * Esa convención es la que hace que el giro sea una sola línea de transform y no
 * cuatro versiones de cada dibujo: el mueble nace con la espalda contra la pared
 * de arriba, y girar 90° lo deja contra la de la izquierda.
 *
 * El tercer parámetro es el mueble ya puesto, y llega sólo cuando existe: la
 * miniatura del catálogo dibuja un modelo, no una pieza del plano. Lo usa el
 * símbolo «a medida» para leer sus radios de esquina; los demás lo ignoran.
 */
type Simbolo = (w: number, h: number, m?: Mueble) => React.ReactNode;

/** El contorno de todo mueble: el rectángulo que ocupa. */
function marco(w: number, h: number, rx = 0.03) {
  return (
    <rect x={0} y={0} width={w} height={h} rx={rx}
          fill="#ffffff" fillOpacity={0.5}
          stroke="currentColor" strokeWidth={TRAZO} />
  );
}

const linea = (x1: number, y1: number, x2: number, y2: number, k = FINO) => (
  <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="currentColor" strokeWidth={k} />
);

/**
 * Una letra centrada en la caja: la rotulación de los electrodomésticos.
 *
 * Es lo que pidió Daniel el 5-oct-2026 para los tres que no se distinguen por su
 * forma —«que en la secadora venga una S en el centro y la lavadora venga una L
 * en el centro […] en la refrigeradora sería una R»—. Un tambor dibujado es el
 * mismo tambor en las dos máquinas; la letra es la convención que usan los
 * planos de verdad y no deja duda.
 *
 * El `rotate` inverso es lo que hace que la letra se LEA: el símbolo entero gira
 * con el mueble, y sin esta vuelta una lavadora puesta contra la pared de abajo
 * mostraría la L de cabeza. El tamaño sale del lado corto para que siga
 * calzando si alguien le cambia la medida en el panel.
 */
function letra(t: string, w: number, h: number, m?: Mueble) {
  const giro = m?.giro ?? 0;
  return (
    <text x={w / 2} y={h / 2} textAnchor="middle" dominantBaseline="central"
          fontSize={Math.min(w, h) * 0.46} fontWeight={600}
          fill="currentColor" style={{ userSelect: 'none' }}
          transform={giro ? `rotate(${-giro} ${w / 2} ${h / 2})` : undefined}>
      {t}
    </text>
  );
}

/**
 * El radio más grande que admite una esquina: la mitad del lado corto. Pasado
 * de ahí los arcos de dos esquinas vecinas se comen entre sí y el contorno se
 * deforma, así que el campo del panel topa acá en vez de dejar dibujar basura.
 */
export const topeRadio = (w: number, h: number) => Math.min(w, h) / 2;

/**
 * El contorno de una caja con un radio propio por esquina, en orden
 * `[no, ne, se, so]`.
 *
 * Va como `path` y no como `rect` porque `rx` de SVG es uno solo para las
 * cuatro, y lo que pidió Daniel es «uno o más bordes redondeados» —poder
 * redondear sólo la esquina que choca con algo y dejar las otras en escuadra—.
 * Un radio en 0 queda recto: la especificación de SVG manda tratar el arco de
 * radio cero como una línea.
 */
function rectRadios(w: number, h: number, r: readonly number[]) {
  const tope = topeRadio(w, h);
  const [a, b, c, d] = [0, 1, 2, 3].map((i) => Math.min(Math.max(r[i] ?? 0, 0), tope));
  return `M ${a} 0 H ${w - b} A ${b} ${b} 0 0 1 ${w} ${b}`
       + ` V ${h - c} A ${c} ${c} 0 0 1 ${w - c} ${h}`
       + ` H ${d} A ${d} ${d} 0 0 1 0 ${h - d}`
       + ` V ${a} A ${a} ${a} 0 0 1 ${a} 0 Z`;
}

/** Las cuatro esquinas en escuadra, y la de muestra de la miniatura «a medida». */
const CERO = [0, 0, 0, 0] as const;
const MUESTRA = [0, 0.18, 0, 0] as const;

// ── LA SIMBOLOGÍA ELÉCTRICA DE ADELANTE ──────────────────────────────────────
//
// Las siete salidas salen de la lámina que mandó Daniel el 9-oct-2026
// (`ad-simbologia-electrica-lamina.svg`), redibujadas con sus proporciones:
// el círculo de 50 unidades de diámetro, la pastilla de adentro de 20, el
// triángulo equilátero inscrito, la barra de 50 × 5. La UNIDAD es `h / 70`
// —los 70 que mide de alto el tomacorriente completo, pata incluida—, así que
// todas conservan entre sí el mismo tamaño relativo que tienen en la lámina.
//
// ⚠️ El LADO DE LA PARED es el NORTE del dibujo (y = 0), que es el reverso de
// la convención del mobiliario —donde el sur es el frente—. Es la misma
// convención vista del otro lado: la cara que un sofá ofrece al cuarto es su
// frente, y la que un tomacorriente ofrece al cuarto es la OPUESTA a su
// espalda contra el muro.
//
// Y lo que va contra el muro es el CÍRCULO, con las patas saliendo hacia
// adentro del cuarto. Lo corrigió Daniel el 9-oct-2026 contra un plano
// eléctrico de verdad —«los círculos salen pegados hacia la pared, las líneas,
// y más bien debería ser el círculo»—: yo lo había deducido al revés de la
// lámina, que es un catálogo y no tiene pared que mirar. La barra de las
// salidas de voz y datos SÍ va contra el muro: esa barra ES la placa.
//
// Van dibujadas sobre el eje de la pared y no dentro de una caja: un símbolo
// eléctrico no ocupa superficie —el tomacorriente real mide 7 cm y sería
// invisible—, es una MARCA de tamaño convencional. Ver TAM_ELECTRICO.

/** La unidad de la lámina: el alto completo del tomacorriente son 70. */
const uni = (h: number) => h / 70;

/** El círculo de los tomacorrientes, apoyado contra la pared: el borde de arriba. */
function discoElectrico(w: number, h: number) {
  const r = uni(h) * 25;
  return { cx: w / 2, cy: r, r };
}

/**
 * Un rótulo que se LEE aunque la pieza esté girada contra cualquier pared.
 *
 * Misma vuelta inversa que `letra`, y por la misma razón: el símbolo entero
 * gira con el mueble, y un «WP» de cabeza no es un rótulo, es una mancha.
 */
function rotulo(t: string, x: number, y: number, tam: number, m: Mueble | undefined,
                ancla: 'middle' | 'end' = 'middle') {
  const giro = m?.giro ?? 0;
  return (
    // `y` es el CENTRO de las letras, no su línea base: con la línea base hay
    // que restarle a ojo la altura de la mayúscula, y eso fue lo que dejó el
    // «TV» corrido dentro de su círculo. `central` lo centra el navegador.
    <text x={x} y={y} textAnchor={ancla} dominantBaseline="central"
          fontSize={tam} fontWeight={700}
          fill="currentColor" style={{ userSelect: 'none' }}
          transform={giro ? `rotate(${-giro} ${x} ${y})` : undefined}>
      {t}
    </text>
  );
}

/**
 * El cuerpo que comparten los tres tomacorrientes de 120 V: el círculo contra
 * la pared, la pastilla de adentro y las dos patas saliendo hacia el cuarto.
 *
 * Lo único que los distingue en la lámina es el RELLENO de la pastilla —blanca
 * el de 0,30 m, negra el de 1,10 m— y el rótulo WP del de agua. Por eso es una
 * sola función con un parámetro y no tres dibujos parecidos que se desincronizan.
 */
function toma120(w: number, h: number, relleno: string) {
  const { cx, cy, r } = discoElectrico(w, h);
  const a = r * 0.4;          // media pastilla
  const yA = cy - r * 0.92;   // donde la pastilla deja de ser recta
  const yB = cy + r * 0.92;
  const b = r * 0.112;        // cuánto se pasan los arcos de las puntas
  return (
    <>
      <circle cx={cx} cy={cy} r={r} fill="#ffffff"
              stroke="currentColor" strokeWidth={TRAZO} />
      <path d={`M ${cx - a} ${yA} L ${cx - a} ${yB}`
             + ` C ${cx - a * 0.36} ${yB + b} ${cx + a * 0.36} ${yB + b} ${cx + a} ${yB}`
             + ` L ${cx + a} ${yA}`
             + ` C ${cx + a * 0.36} ${yA - b} ${cx - a * 0.36} ${yA - b} ${cx - a} ${yA} Z`}
            fill={relleno} stroke="currentColor" strokeWidth={FINO} />
      {/* Las dos patas: de la punta de la pastilla que toca el muro hasta el
          otro extremo de la marca, saliendo hacia adentro del cuarto. */}
      <line x1={cx - a} y1={yA} x2={cx - a} y2={h}
            stroke="currentColor" strokeWidth={FINO} />
      <line x1={cx + a} y1={yA} x2={cx + a} y2={h}
            stroke="currentColor" strokeWidth={FINO} />
    </>
  );
}

/** El triángulo equilátero de la lámina, inscrito en un círculo de radio `r`. */
const triangulo = (cx: number, cy: number, r: number) =>
  `M ${cx} ${cy - r} L ${cx + r * 0.866} ${cy + r / 2}`
  + ` L ${cx - r * 0.866} ${cy + r / 2} Z`;

/**
 * Las salidas de voz y datos: la barra contra el muro y el triángulo colgando.
 *
 * Difieren sólo en el relleno —negro la de voz, blanco la de datos—, igual que
 * los dos tomacorrientes de 120 V.
 */
function salidaTriangular(w: number, h: number, relleno: string) {
  const u = uni(h);
  const cx = w / 2;
  const r = u * 25;             // el mismo radio que el disco, para que midan igual
  const barra = u * 5.146;
  const alto = u * 37.5;        // alto del triángulo de la lámina
  const base = u * 43.3;
  return (
    <>
      <path d={`M ${cx} ${barra} L ${cx + base / 2} ${barra + alto}`
             + ` L ${cx - base / 2} ${barra + alto} Z`}
            fill={relleno} stroke="currentColor" strokeWidth={TRAZO} />
      <rect x={cx - r} y={0} width={r * 2} height={barra}
            fill={relleno} stroke="currentColor" strokeWidth={FINO} />
    </>
  );
}

const SIMBOLOS: Record<string, Simbolo> = {
  /**
   * Clóset: la varilla de la ropa paralela al fondo y las dos hojas corredizas
   * en el frente. Es lo que lo distingue de un mueble bajo cualquiera.
   */
  closet: (w, h) => (
    <>
      {marco(w, h)}
      {linea(0.06, h * 0.42, w - 0.06, h * 0.42)}
      {/* Las hojas: dos bandas desfasadas sobre el frente, que es como se dibuja
          una corrediza en planta —una por delante de la otra—. */}
      <rect x={0.04} y={h - 0.1} width={w / 2 - 0.04} height={0.06}
            fill="currentColor" fillOpacity={0.35} />
      <rect x={w / 2} y={h - 0.16} width={w / 2 - 0.04} height={0.06}
            fill="currentColor" fillOpacity={0.35} />
    </>
  ),

  /** Mueble bajo de cocina: el tope y la línea de la puerta. */
  mueble: (w, h) => (
    <>
      {marco(w, h)}
      {linea(0.05, h - 0.09, w - 0.05, h - 0.09)}
      {linea(w / 2, h - 0.09, w / 2, h - 0.03)}
    </>
  ),

  /**
   * El módulo de cocina con los cuatro discos. Lo pidió Daniel el 5-oct-2026:
   * «que lo que es el módulo de cocina, que se vean los cuatro discos, que son
   * cuatro círculos».
   *
   * Va sólo en el de 60 × 60 —la medida de una estufa— y no en el de 80 × 60,
   * que queda gabinete liso: con los discos en los dos, una fila corrida de
   * cocina saldría con ocho hornillas.
   *
   * Los centros van en fracciones de `w` y `h` y los radios en el lado corto,
   * así el dibujo sigue leyéndose si alguien le cambia la medida en el panel.
   */
  estufa: (w, h) => {
    const r = Math.min(w, h) * 0.145;
    const xs = [w * 0.29, w * 0.71];
    const ys = [h * 0.26, h * 0.6];
    return (
      <>
        {marco(w, h)}
        {ys.flatMap((cy, i) => xs.map((cx, j) => (
          <circle key={`${i}-${j}`} cx={cx} cy={cy} r={r}
                  fill="currentColor" fillOpacity={0.18}
                  stroke="currentColor" strokeWidth={FINO} />
        )))}
        {linea(0.05, h - 0.09, w - 0.05, h - 0.09)}
      </>
    );
  },

  /** Fregadero: la poza y el grifo contra la pared. */
  fregadero: (w, h) => (
    <>
      {marco(w, h)}
      <rect x={w * 0.14} y={h * 0.26} width={w * 0.72} height={h * 0.5} rx={0.04}
            fill="none" stroke="currentColor" strokeWidth={FINO} />
      <circle cx={w / 2} cy={h * 0.14} r={0.045} fill="none"
              stroke="currentColor" strokeWidth={FINO} />
      {linea(w / 2, h * 0.14, w / 2, h * 0.26)}
    </>
  ),

  /**
   * Refrigeradora: el cuadrado con su R, y el arco de apertura.
   *
   * La R la pidió Daniel el 5-oct-2026, por lo mismo que la L y la S. El arco se
   * queda porque dice de qué lado abre —el dato que uno quiere ver en una cocina
   * apretada— y porque se dibuja AFUERA de la caja, así que no le pelea el
   * centro a la letra.
   */
  refri: (w, h, m) => (
    <>
      {marco(w, h, 0)}
      {letra('R', w, h, m)}
      <path d={`M ${w - 0.05} ${h} A ${w * 0.62} ${w * 0.62} 0 0 1 ${w - 0.05} ${h + w * 0.5}`}
            fill="none" stroke="currentColor" strokeWidth={FINO}
            strokeDasharray="0.06 0.05" />
      {linea(w - 0.05, h, w - 0.05, h + w * 0.5, FINO)}
    </>
  ),

  /** Cama: la cabecera al norte, con sus almohadas y el doblez de la sábana. */
  cama: (w, h) => {
    const dos = w > 1.2;                       // de 1,20 para arriba van dos
    const aw = dos ? w / 2 - 0.1 : w * 0.56;
    return (
      <>
        {marco(w, h, 0.05)}
        <rect x={dos ? 0.06 : (w - aw) / 2} y={0.07} width={aw} height={0.3} rx={0.06}
              fill="none" stroke="currentColor" strokeWidth={FINO} />
        {dos && (
          <rect x={w / 2 + 0.04} y={0.07} width={aw} height={0.3} rx={0.06}
                fill="none" stroke="currentColor" strokeWidth={FINO} />
        )}
        {linea(0.03, h * 0.42, w - 0.03, h * 0.42)}
      </>
    );
  },

  /** Inodoro: el tanque contra la pared y la taza. */
  inodoro: (w, h) => (
    <>
      <rect x={0} y={0} width={w} height={h * 0.26} rx={0.03}
            fill="#ffffff" fillOpacity={0.5} stroke="currentColor" strokeWidth={TRAZO} />
      <ellipse cx={w / 2} cy={h * 0.62} rx={w * 0.42} ry={h * 0.33}
               fill="#ffffff" fillOpacity={0.5} stroke="currentColor" strokeWidth={TRAZO} />
      <ellipse cx={w / 2} cy={h * 0.62} rx={w * 0.26} ry={h * 0.2}
               fill="none" stroke="currentColor" strokeWidth={FINO} />
    </>
  ),

  /** Lavatorio: la poza y el grifo. */
  lavatorio: (w, h) => (
    <>
      {marco(w, h, 0.04)}
      <ellipse cx={w / 2} cy={h * 0.58} rx={w * 0.34} ry={h * 0.3}
               fill="none" stroke="currentColor" strokeWidth={FINO} />
      <circle cx={w / 2} cy={h * 0.16} r={0.04} fill="none"
              stroke="currentColor" strokeWidth={FINO} />
    </>
  ),

  /**
   * Ducha: el recinto, el PLATO adentro con su reborde, y el desagüe.
   *
   * Lo pidió Daniel el 5-oct-2026: «que se vea el plato de la ducha para que se
   * entienda pues mejor». Antes eran dos diagonales sueltas en un cuadrado, que
   * es también como se dibuja un vacío o una losa; el reborde del plato es lo
   * que la vuelve inconfundiblemente una ducha —el escalón del receptáculo— y
   * las diagonales pasan a ser lo que son en obra: la pendiente al desagüe.
   */
  ducha: (w, h) => {
    const b = Math.min(w, h) * 0.08;           // el reborde del plato
    const a = b + 0.03;                        // donde arrancan las diagonales
    return (
      <>
        {marco(w, h, 0)}
        <rect x={b} y={b} width={w - 2 * b} height={h - 2 * b} rx={0.03}
              fill="#ffffff" fillOpacity={0.6}
              stroke="currentColor" strokeWidth={FINO} />
        {linea(a, a, w - a, h - a)}
        {linea(w - a, a, a, h - a)}
        <circle cx={w / 2} cy={h / 2} r={0.055}
                fill="#ffffff" stroke="currentColor" strokeWidth={FINO} />
        {/* La REGADERA, contra el lado que toca la pared. Daniel, 5-oct-2026:
            «no se ve la regadera […] tal vez por eso es que le cuesta
            identificarlo y lo reconoce como si fuera un inodoro». Sin ella el
            símbolo era una X con un punto —que es como se dibuja un vacío— y
            nada decía "acá uno se baña". El brazo sale de la pared y la roseta
            apunta al plato. */}
        {linea(w / 2, b, w / 2, b + ROSETA * 0.9)}
        <path d={`M ${w / 2 - ROSETA} ${b + ROSETA * 0.9}`
                 + ` A ${ROSETA} ${ROSETA} 0 0 0 ${w / 2 + ROSETA} ${b + ROSETA * 0.9} Z`}
              fill="#ffffff" stroke="currentColor" strokeWidth={FINO} />
      </>
    );
  },

  /** Lavadora: el cuadrado pelado con su L. Ver `letra`. */
  lavadora: (w, h, m) => (
    <>
      {marco(w, h, 0)}
      {letra('L', w, h, m)}
    </>
  ),

  /** Secadora: el cuadrado pelado con su S. Ver `letra`. */
  secadora: (w, h, m) => (
    <>
      {marco(w, h, 0)}
      {letra('S', w, h, m)}
    </>
  ),

  /** Mesa: el tablero, sin más. Lo que la hace mesa es su proporción. */
  mesa: (w, h) => (
    <>
      {marco(w, h, 0.05)}
      <rect x={0.1} y={0.1} width={w - 0.2} height={h - 0.2} rx={0.04}
            fill="none" stroke="currentColor" strokeWidth={FINO} />
    </>
  ),

  /**
   * Mesa redonda: el tablero y el borde de adentro, en elipse.
   *
   * Va en elipse y no en círculo para que siga sirviendo si alguien la estira a
   * ovalada desde el panel; con la medida de catálogo —cuadrada— sale redonda.
   * NO lleva `marco`: el rectángulo que la contiene no es parte del mueble, y
   * dibujarlo la leería como una mesa cuadrada con un círculo encima.
   */
  mesaRedonda: (w, h) => {
    const rx = Math.max(0.02, w / 2 - TRAZO / 2);
    const ry = Math.max(0.02, h / 2 - TRAZO / 2);
    return (
      <>
        <ellipse cx={w / 2} cy={h / 2} rx={rx} ry={ry}
                 fill="#ffffff" fillOpacity={0.5}
                 stroke="currentColor" strokeWidth={TRAZO} />
        <ellipse cx={w / 2} cy={h / 2}
                 rx={Math.max(0.01, rx - 0.09)} ry={Math.max(0.01, ry - 0.09)}
                 fill="none" stroke="currentColor" strokeWidth={FINO} />
      </>
    );
  },

  /**
   * Sofá en L: el brazo largo contra la pared de arriba y el de vuelta contra la
   * de la izquierda, con el rincón abierto al sur-este.
   *
   * La L se dibuja con un `path` y no con dos rectángulos porque el contorno
   * tiene que ser UNO: dos cajas dejarían la costura del rincón marcada por
   * dentro, que es justo donde no hay nada.
   *
   * La profundidad de los dos brazos sale del lado corto, así que girarlo o
   * estirarlo no lo deforma: queda un sofá más largo, no uno más gordo.
   */
  sofaL: (w, h) => {
    const p = Math.min(w, h) * 0.52;           // la profundidad de cada brazo
    const b = Math.min(p * 0.32, 0.26);        // el respaldo y los brazos
    return (
      <>
        <path d={`M 0 0 H ${w} V ${p} H ${p} V ${h} H 0 Z`}
              fill="#ffffff" fillOpacity={0.5}
              stroke="currentColor" strokeWidth={TRAZO} />
        {/* Los dos respaldos, al norte y al oeste. */}
        <rect x={0.03} y={0.03} width={w - 0.06} height={b} rx={0.04}
              fill="currentColor" fillOpacity={0.3} />
        <rect x={0.03} y={0.03 + b} width={b} height={h - 0.06 - b} rx={0.04}
              fill="currentColor" fillOpacity={0.3} />
        {/* Y un brazo en cada punta libre de la L. */}
        <rect x={w - 0.03 - b} y={0.03 + b} width={b}
              height={Math.max(0.02, p - 0.06 - b)} rx={0.04}
              fill="currentColor" fillOpacity={0.3} />
        <rect x={0.03 + b} y={h - 0.03 - b}
              width={Math.max(0.02, p - 0.06 - b)} height={b} rx={0.04}
              fill="currentColor" fillOpacity={0.3} />
        {/* La costura que separa el asiento del rincón del resto del brazo. */}
        {linea(p, 0.03 + b, p, p)}
      </>
    );
  },

  /** Silla: el asiento y el respaldo. */
  silla: (w, h) => (
    <>
      {marco(w, h, 0.04)}
      <rect x={0.03} y={0.03} width={w - 0.06} height={h * 0.2} rx={0.03}
            fill="currentColor" fillOpacity={0.3} />
    </>
  ),

  /** Sofá: el respaldo al norte y los dos brazos. */
  sofa: (w, h) => (
    <>
      {marco(w, h, 0.05)}
      <rect x={0.03} y={0.03} width={w - 0.06} height={h * 0.26} rx={0.04}
            fill="currentColor" fillOpacity={0.3} />
      <rect x={0.03} y={0.03} width={w * 0.1} height={h - 0.06} rx={0.04}
            fill="currentColor" fillOpacity={0.3} />
      <rect x={w - 0.03 - w * 0.1} y={0.03} width={w * 0.1} height={h - 0.06} rx={0.04}
            fill="currentColor" fillOpacity={0.3} />
      {linea(w / 2, h * 0.3, w / 2, h - 0.05)}
    </>
  ),

  /**
   * El objeto «a medida»: el contorno pelado, con el radio que tenga cada
   * esquina. No lleva detalle interno a propósito —no representa ningún mueble
   * en particular—: es la pieza que se estira para lo que el catálogo no tiene.
   *
   * Sin mueble —o sea, en la miniatura del catálogo— dibuja una esquina
   * redondeada de muestra, que es lo que anuncia de qué se trata. Un mueble ya
   * puesto sin radios sale en escuadra: el `m ?` distingue «no hay pieza» de
   * «la pieza no tiene radios», que no es lo mismo.
   */
  medida: (w, h, m) => (
    <path d={rectRadios(w, h, m ? m.radios ?? CERO : MUESTRA)}
          fill="#ffffff" fillOpacity={0.5}
          stroke="currentColor" strokeWidth={TRAZO} />
  ),

  // ── ELÉCTRICO ────────────────────────────────────────────────────────────
  //
  // Las siete de la lámina de Daniel. Lo que las dibuja está arriba, junto a
  // la convención de la pared al norte; acá sólo se nombran y se rellenan.

  /** 1. Tomacorriente 120 V: la pastilla en blanco. */
  'toma-120': (w, h) => toma120(w, h, '#ffffff'),

  /** 2. Tomacorriente 120 V alto: la misma pieza con la pastilla llena. */
  'toma-120-alto': (w, h) => toma120(w, h, 'currentColor'),

  /** 3. Tomacorriente a prueba de agua: el de 120 V rotulado WP. */
  'toma-wp': (w, h, m) => (
    <>
      {toma120(w, h, '#ffffff')}
      {/* El rótulo va al costado, como en la lámina. Abajo a la derecha es el
          único hueco que queda: el círculo se come la mitad de arriba contra
          la pared y las patas bajan por el centro. */}
      {rotulo('WP', w, h * 0.87, h * 0.17, m, 'end')}
    </>
  ),

  /** 4. Tomacorriente 240 V 50 A: el disco con el triángulo lleno. */
  'toma-240': (w, h) => {
    const { cx, cy, r } = discoElectrico(w, h);
    return (
      <>
        <circle cx={cx} cy={cy} r={r} fill="#ffffff"
                stroke="currentColor" strokeWidth={TRAZO} />
        <path d={triangulo(cx, cy, r)} fill="currentColor"
              stroke="currentColor" strokeWidth={FINO} />
      </>
    );
  },

  /** 5. Salida de voz RJ11: el triángulo lleno bajo la barra. */
  'voz-rj11': (w, h) => salidaTriangular(w, h, 'currentColor'),

  /** 6. Salida de datos RJ45: la misma pieza en blanco. */
  'datos-rj45': (w, h) => salidaTriangular(w, h, '#ffffff'),

  /** 7. Salida de TV coaxial: el disco rotulado. */
  'tv-coaxial': (w, h, m) => {
    const { cx, cy, r } = discoElectrico(w, h);
    return (
      <>
        <circle cx={cx} cy={cy} r={r} fill="#ffffff"
                stroke="currentColor" strokeWidth={TRAZO} />
        {rotulo('TV', cx, cy, r * 0.8, m)}
      </>
    );
  },

  /**
   * Apagador: la palanca que sale de la pared, con la S de «switch».
   *
   * ⚠️ PROVISIONAL: la lámina del 9-oct-2026 trae las siete salidas pero
   * NINGÚN apagador, y Daniel sí los nombró en el pedido original («ciertos
   * objetos, como son tomacorrientes, apagadores»). Queda dibujado a mi
   * criterio hasta que mande el suyo; lo que no cambia es la máquina.
   */
  apagador: (w, h) => (
    <>
      <line x1={w / 2} y1={0} x2={w / 2} y2={h * 0.55}
            stroke="currentColor" strokeWidth={TRAZO} />
      <line x1={w / 2} y1={h * 0.55} x2={w * 0.95} y2={h * 0.82}
            stroke="currentColor" strokeWidth={TRAZO} />
      <text x={w * 0.2} y={h * 0.42} fontSize={w * 0.55} fontWeight={700}
            textAnchor="middle" dominantBaseline="middle"
            fill="currentColor" style={{ userSelect: 'none' }}>S</text>
    </>
  ),

  /** Luz de cielo raso: el círculo con la cruz, centrado en el punto. */
  luz: (w, h) => (
    <>
      <circle cx={w / 2} cy={h / 2} r={w * 0.38}
              fill="#ffffff" stroke="currentColor" strokeWidth={TRAZO} />
      <line x1={w * 0.12} y1={h / 2} x2={w * 0.88} y2={h / 2}
            stroke="currentColor" strokeWidth={FINO} />
      <line x1={w / 2} y1={h * 0.12} x2={w / 2} y2={h * 0.88}
            stroke="currentColor" strokeWidth={FINO} />
    </>
  ),
};

export interface ModeloMueble {
  clave: string;
  nombre: string;
  /** Lo que ocupa con giro 0, en metros: `w` a lo ancho, `h` en profundidad. */
  w: number;
  h: number;
  simbolo: string;
  grupo: string;
  /** Puesto sólo cuando la medida NO la dictó Daniel: es la que hay que confirmar. */
  propuesta?: true;
  /**
   * La pieza existe en la paleta pero el PUNTAJE no la elige nunca: al plano
   * sólo llega por una huella compuesta, o puesta a mano.
   *
   * Hace falta porque una pieza chica le gana por centímetros a media docena de
   * vecinas. Medido sobre los 50 modelos publicados: sumar la mesa de noche
   * (0,40 × 0,40) al catálogo sin este freno convertía en mesa de noche las 26
   * sillas (0,45 × 0,45) y 5 de los 114 lavatorios. Es la misma razón por la
   * que el puntaje tampoco mira lo `electrico`.
   */
  soloHuella?: true;
  /**
   * El único modelo que conserva agarres sobre el plano.
   *
   * El resto se mide SÓLO desde la paleta flotante, porque Daniel lo pidió así
   * el 5-oct-2026: «es muy fácil darle clic donde uno está moviéndolo y se
   * amplía sin intención». Y en la misma dictada pidió lo contrario para una
   * pieza: «tal vez sí me gustaría que existiera un objeto que sea
   * customizable […] a ese sí se le puede ampliar cualquiera de los lados».
   *
   * Las dos cosas conviven porque los agarres se reparten por función: los
   * cuatro de los LADOS estiran y los cuatro de las ESQUINAS abren el radio.
   * Así el gesto resbaladizo —la esquina, que es la que se tocaba sin querer—
   * deja de significar «estirar» incluso donde los agarres siguen existiendo.
   */
  flexible?: true;
  /**
   * La pieza SÓLO se reconoce en un bulto que traiga desagüe.
   *
   * Es un filtro, no un premio: el plato de ducha y el inodoro del mismo baño
   * miden casi lo mismo —0,64 × 0,38 contra 0,64 × 0,43 en el Burgos— y se
   * disputan el bulto por un centímetro. Pedir el desagüe saca al plato de la
   * disputa cuando el bulto es el inodoro, que no tiene ninguno.
   *
   * Premiarlo además sería peor: medido sobre el catálogo, el lavatorio de los
   * Zante y los Santorini (0,45 × 0,40) TAMBIÉN trae su desagüe, y un premio lo
   * convertía en ducha. Filtrando y dejando que mande el tamaño, el lavatorio
   * le gana al plato por 11 cm, que es como debe ser.
   */
  desague?: true;
  /**
   * La forma de las esquinas, sólo en las piezas GUARDADAS por el usuario a
   * partir del módulo «a medida».
   *
   * El catálogo de fábrica no la usa —sus piezas nacen en escuadra— pero una
   * pieza guardada sí: lo que Daniel moldea en el módulo variable es tanto la
   * medida como el recorte de las esquinas, y guardar sólo la medida devolvería
   * un rectángulo donde él dejó un chaflán. `crearMueble` la copia al nacer.
   */
  radios?: [number, number, number, number];
  /**
   * La pieza es una SALIDA ELÉCTRICA, no un mueble.
   *
   * Cambia tres cosas: se cuenta en el resumen del botón ⓘ, se dibuja con su
   * propia tinta, y no se le ofrecen los agarres de estirar —un tomacorriente
   * no tiene medida que editar, tiene ALTURA—.
   */
  electrico?: true;
  /**
   * Nace pegada a la pared más cercana, por la cara que mira hacia adentro.
   *
   * Es la mitad del pedido de Daniel del 9-oct-2026; la otra mitad —«ponerlo en
   * el centro de cualquier área o en algún punto que uno defina»— no necesita
   * bandera: soltar la pieza lejos de toda pared ya la deja libre. Las luces
   * NO la llevan: «eso iría más bien directamente en los espacios, eso no tiene
   * el comportamiento de paredes».
   */
  pegaPared?: true;
  /**
   * Altura de montaje de fábrica, en metros sobre piso terminado. Lo que se
   * rotula al lado del símbolo en un plano eléctrico. Editable por pieza; ver
   * `altura` en `Mueble`.
   */
  alturaDefecto?: number;
  /**
   * El dibujo PROPIO de la pieza, cuando lo trajo el usuario.
   *
   * `simbolo` es una LLAVE al mapa de los ~30 dibujos escritos a mano, y eso
   * dejaba al usuario sin salida: una pieza guardada por él tenía que pedir
   * prestado el dibujo de otra, y la vitrina terminaba mostrando un sofá donde
   * él había guardado un lavatorio.
   *
   * Con `trazo` puesto manda el trazo y `simbolo` queda de respaldo —para una
   * versión vieja del código, o si el dibujo importado resultara ilegible—. Por
   * eso siguen conviviendo en vez de reemplazarse.
   */
  trazo?: Trazo;
}

/**
 * El catálogo. El orden es el de uso, no el alfabético: lo de arriba es lo que
 * se pone en toda casa.
 *
 * La medida con la que nace cada módulo NO es una jaula —Daniel fue explícito:
 * «que se puedan editar, si uno desea, las dos dimensiones»—, es el caso
 * corriente aceptado sin escribirlo.
 */
export const CATALOGO: ModeloMueble[] = [
  // Lo que dictó para dormitorios.
  { clave: 'closet', nombre: 'Clóset', w: 1.5, h: 0.6, simbolo: 'closet', grupo: 'Dormitorio' },
  { clave: 'cama-individual', nombre: 'Cama individual', w: 1.0, h: 1.9, simbolo: 'cama', grupo: 'Dormitorio' },
  // Medida en los croquis del catálogo, no dictada: el dormitorio secundario de
  // Burgos trae 1,30 × 1,90 y el de Ávila 1,35 × 1,90. Sin esta pieza esas camas
  // erraban por 30 cm contra la individual y caían a «a medida».
  { clave: 'cama-matrimonial', nombre: 'Cama matrimonial', w: 1.35, h: 1.9, simbolo: 'cama', grupo: 'Dormitorio' },
  { clave: 'cama-queen', nombre: 'Cama queen', w: 1.6, h: 1.9, simbolo: 'cama', grupo: 'Dormitorio' },
  { clave: 'cama-king', nombre: 'Cama king', w: 2.0, h: 2.0, simbolo: 'cama', grupo: 'Dormitorio' },
  // Nunca sale sola de un plano: el croquis la dibuja PEGADA a la cama, en un
  // solo bloque. Existe para que la huella compuesta tenga dónde ponerla —ver
  // `HUELLAS_FABRICA`— y para que se pueda poner a mano en un dormitorio.
  { clave: 'mesa-noche', nombre: 'Mesa de noche', w: 0.4, h: 0.4, simbolo: 'mueble',
    grupo: 'Dormitorio', soloHuella: true },

  // Cocina.
  { clave: 'cocina', nombre: 'Módulo de cocina', w: 0.6, h: 0.6, simbolo: 'estufa', grupo: 'Cocina' },
  { clave: 'cocina-grande', nombre: 'Módulo de cocina grande', w: 0.8, h: 0.6, simbolo: 'mueble', grupo: 'Cocina' },
  { clave: 'fregadero', nombre: 'Módulo de fregadero', w: 0.8, h: 0.6, simbolo: 'fregadero', grupo: 'Cocina' },
  { clave: 'refri', nombre: 'Refrigeradora', w: 0.9, h: 0.9, simbolo: 'refri', grupo: 'Cocina' },

  // Baño. Inodoro y lavatorio los dejó en «estándar»: estas son las medidas
  // corrientes de obra en Costa Rica, a confirmar.
  { clave: 'inodoro', nombre: 'Inodoro', w: 0.4, h: 0.7, simbolo: 'inodoro', grupo: 'Baño', propuesta: true },
  { clave: 'lavatorio', nombre: 'Lavatorio', w: 0.5, h: 0.45, simbolo: 'lavatorio', grupo: 'Baño', propuesta: true },
  { clave: 'ducha', nombre: 'Ducha', w: 0.9, h: 0.9, simbolo: 'ducha', grupo: 'Baño' },
  // La ducha que de verdad trae el catálogo: 0,49 × 0,91 en Ávila, 0,50 × 1,00
  // en Burgos. Es la que Daniel vio salir como inodoro, y con razón —contra el
  // inodoro de 0,40 × 0,70 erraba 21 cm, justo por debajo de la tolerancia—.
  { clave: 'ducha-rect', nombre: 'Ducha rectangular', w: 0.5, h: 0.9, simbolo: 'ducha', grupo: 'Baño' },
  // EL PLATO DE DUCHA, que es lo que el plano dibuja de verdad en casi todos los
  // baños: no el contorno del plato sino el DESAGÜE con su llave, un anillo de
  // 10 cm con el brazo hasta la pared. Daniel mandó el recorte el 9-oct-2026
  // —«es la referencia del plato de ducha que aparece en todos los PDFs, pero a
  // la hora de identificarlo lo convierte como si fuera un inodoro»— y tenía
  // razón: ese grupo de tinta mide 0,58 × 0,29 en el Salamanca y 0,64 × 0,38 en
  // el Burgos, contra un inodoro de 0,64 × 0,43 en el MISMO baño. Por tamaño no
  // se pueden separar: lo que los separa es el desagüe, y por eso esta fila lo
  // exige. Ver `desague` en `Bulto`.
  //
  // 0,62 × 0,36 no es el promedio de los dos tamaños sino el punto que le gana
  // al inodoro en LOS DOS: contra el inodoro girado (0,70 × 0,40) el 0,58 × 0,29
  // erra 12 cm y el 0,64 × 0,38 erra 6, así que el plato tiene que quedar por
  // debajo de las dos cifras a la vez. Centrarlo en el tamaño más común
  // —0,58 × 0,29, que sale 31 veces en el catálogo contra 2— le devolvía el
  // Burgos al inodoro.
  { clave: 'ducha-plato', nombre: 'Plato de ducha', w: 0.62, h: 0.36, simbolo: 'ducha', grupo: 'Baño', desague: true },

  // Pilas.
  { clave: 'lavadora', nombre: 'Lavadora', w: 0.7, h: 0.7, simbolo: 'lavadora', grupo: 'Pilas' },
  { clave: 'secadora', nombre: 'Secadora', w: 0.7, h: 0.7, simbolo: 'secadora', grupo: 'Pilas' },

  // «Bueno, mesas, sillas» — sin medida dictada.
  { clave: 'mesa-4', nombre: 'Mesa de 4', w: 1.5, h: 0.9, simbolo: 'mesa', grupo: 'Sala', propuesta: true },
  { clave: 'mesa-6', nombre: 'Mesa de 6', w: 1.8, h: 1.0, simbolo: 'mesa', grupo: 'Sala', propuesta: true },
  // Los dos que pidió Daniel el 5-oct-2026. Sin medida dictada: la redonda nace
  // de 1,10 (una mesa de cuatro) y la L de 2,40 × 1,80 (la de una sala corriente).
  { clave: 'mesa-redonda', nombre: 'Mesa redonda', w: 1.1, h: 1.1, simbolo: 'mesaRedonda', grupo: 'Sala', propuesta: true },
  { clave: 'silla', nombre: 'Silla', w: 0.45, h: 0.45, simbolo: 'silla', grupo: 'Sala', propuesta: true },
  { clave: 'sofa', nombre: 'Sofá', w: 2.0, h: 0.9, simbolo: 'sofa', grupo: 'Sala', propuesta: true },
  { clave: 'sofa-l', nombre: 'Sofá en L', w: 2.4, h: 1.8, simbolo: 'sofaL', grupo: 'Sala', propuesta: true },
  // El sillón en L que Daniel señaló en la sala. No es una propuesta: 1,20 × 2,00
  // sale IGUAL en Ávila y en Burgos, que es la firma de una pieza repetida del
  // catálogo de la empresa y no una casualidad de un plano.
  { clave: 'sofa-l-chico', nombre: 'Sofá en L chico', w: 1.2, h: 2.0, simbolo: 'sofaL', grupo: 'Sala' },

  // La pieza comodín. Nace cuadrada de 60 —la medida que dictó Daniel— y es la
  // única que se estira con el dedo sobre el plano.
  { clave: 'medida', nombre: 'A medida', w: 0.6, h: 0.6, simbolo: 'medida', grupo: 'A medida', flexible: true },

  // ELÉCTRICO — para las reuniones iniciales de arquitectura con el cliente.
  //
  // ⚠️ Los TRES de abajo son PROVISIONALES y la lista la manda Daniel («yo te
  // paso la lista exacta con la simbología», 9-oct-2026). Esta tanda existe
  // para que el comportamiento se pueda probar y ver hoy; cuando llegue la
  // suya se reemplazan estas filas y sus símbolos, no la máquina.
  //
  // Las alturas son las corrientes de obra en Costa Rica. Van como DEFECTO, no
  // como jaula: Daniel las pidió «con altura por defecto según el tipo y
  // editable en la paleta».
  //
  // Las dos primeras pegan a pared y la luz no, que es exactamente como lo
  // dictó: «en el tema de luces, eso iría más bien directamente en los
  // espacios, eso no tiene el comportamiento de paredes».
  // En el ORDEN DE LA LÁMINA, que es el que usa el departamento y por lo tanto
  // también el orden en que las lista el resumen del botón ⓘ. Las alturas
  // 0,30 y 1,10 vienen rotuladas en la lámina; las otras cinco las puse en
  // 0,30 —la altura corriente de una salida— y están pendientes de confirmar.
  { clave: 'toma-120', nombre: 'Tomacorriente 120V', w: TAM_ELECTRICO, h: TAM_ELECTRICO,
    simbolo: 'toma-120', grupo: 'Eléctrico',
    electrico: true, pegaPared: true, alturaDefecto: 0.3 },
  { clave: 'toma-120-alto', nombre: 'Tomacorriente 120V alto', w: TAM_ELECTRICO, h: TAM_ELECTRICO,
    simbolo: 'toma-120-alto', grupo: 'Eléctrico',
    electrico: true, pegaPared: true, alturaDefecto: 1.1 },
  { clave: 'toma-wp', nombre: 'Tomacorriente agua WP', w: TAM_ELECTRICO, h: TAM_ELECTRICO,
    simbolo: 'toma-wp', grupo: 'Eléctrico',
    electrico: true, pegaPared: true, alturaDefecto: 0.3, propuesta: true },
  { clave: 'toma-240', nombre: 'Tomacorriente 240V 50A', w: TAM_ELECTRICO, h: TAM_ELECTRICO,
    simbolo: 'toma-240', grupo: 'Eléctrico',
    electrico: true, pegaPared: true, alturaDefecto: 0.3, propuesta: true },
  { clave: 'voz-rj11', nombre: 'Salida de voz RJ11', w: TAM_ELECTRICO, h: TAM_ELECTRICO,
    simbolo: 'voz-rj11', grupo: 'Eléctrico',
    electrico: true, pegaPared: true, alturaDefecto: 0.3, propuesta: true },
  { clave: 'datos-rj45', nombre: 'Salida de datos RJ45', w: TAM_ELECTRICO, h: TAM_ELECTRICO,
    simbolo: 'datos-rj45', grupo: 'Eléctrico',
    electrico: true, pegaPared: true, alturaDefecto: 0.3, propuesta: true },
  { clave: 'tv-coaxial', nombre: 'Salida de TV coaxial', w: TAM_ELECTRICO, h: TAM_ELECTRICO,
    simbolo: 'tv-coaxial', grupo: 'Eléctrico',
    electrico: true, pegaPared: true, alturaDefecto: 0.3, propuesta: true },
  // El apagador no viene en la lámina y la luz Daniel la aprobó como está
  // («el de luz me parece perfecto», 9-oct-2026).
  { clave: 'apagador', nombre: 'Apagador', w: TAM_ELECTRICO, h: TAM_ELECTRICO,
    simbolo: 'apagador', grupo: 'Eléctrico',
    electrico: true, pegaPared: true, alturaDefecto: 1.2, propuesta: true },
  { clave: 'luz-techo', nombre: 'Luz de cielo raso', w: TAM_ELECTRICO, h: TAM_ELECTRICO,
    simbolo: 'luz', grupo: 'Eléctrico', electrico: true },
];

/**
 * El orden en que la vitrina muestra los grupos.
 *
 * «A medida» va PRIMERA por pedido de Daniel del 9-oct-2026 —«en lugar de
 * tirarlo de último, que sea el primero de la lista, arriba de todos los
 * objetos de mobiliario»—, y tiene sentido: es la única pieza que sirve para
 * lo que el catálogo no tiene, o sea la que uno busca cuando no encontró nada,
 * y estaba al final de un scroll de siete grupos.
 *
 * Este orden es SÓLO de vitrina. El reconocedor del plano no lo mira: filtra
 * por pertenencia (`permitidos.includes`) y no por posición.
 */
export const GRUPOS = ['A medida', 'Dormitorio', 'Cocina', 'Baño', 'Pilas', 'Sala', 'Eléctrico'];

/**
 * Las piezas que guardó Daniel con sus propias medidas.
 *
 * Pedido del 5-oct-2026: «le cambio las medidas o hago con el módulo variable
 * alguna medida en específico y en base a eso lo puedo guardar con un nombre y
 * lo meto a la categoría que desee».
 *
 * Viven en `localStorage` y no en el modelo de la planta, porque NO son parte
 * de la casa: son herramienta de quien dibuja. Guardarlas en la planta las
 * ataría a un modelo y habría que volver a crearlas en el siguiente; guardarlas
 * acá las deja disponibles en toda planta que se abra.
 *
 * ⚠️ Eso también fija su límite, y conviene saberlo antes de confiarle una
 * biblioteca entera: `localStorage` es de ESE navegador. Lo guardado en el
 * celular no aparece en la computadora. Para que viajen hace falta una tabla
 * en AdelanteDB, que es decisión aparte.
 */
const LLAVE_PROPIOS = 'planta-muebles-propios';

/** El grupo al que caen las piezas propias si no se eligió otro. */
export const GRUPO_PROPIO = 'Míos';

export function leerPropios(): ModeloMueble[] {
  try {
    const crudo = localStorage.getItem(LLAVE_PROPIOS);
    if (!crudo) return [];
    const lista = JSON.parse(crudo);
    if (!Array.isArray(lista)) return [];
    // Se filtra pieza por pieza y no se confía en el JSON entero: lo que hay en
    // `localStorage` lo pudo escribir una versión anterior de este código.
    return lista.filter(
      (m): m is ModeloMueble =>
        m && typeof m.clave === 'string' && typeof m.nombre === 'string'
        && typeof m.w === 'number' && typeof m.h === 'number'
        && typeof m.simbolo === 'string' && typeof m.grupo === 'string',
    );
  } catch {
    // Un `localStorage` bloqueado (Safari privado) no es motivo para que el
    // editor no abra: sin piezas propias funciona igual.
    return [];
  }
}

function escribirPropios(lista: ModeloMueble[]) {
  try {
    localStorage.setItem(LLAVE_PROPIOS, JSON.stringify(lista));
  } catch { /* ídem: se pierde el guardado, no el editor */ }
  refrescarIndice();
}

/**
 * Guarda una pieza nueva y devuelve su ficha.
 *
 * La clave se arma con la hora porque tiene que ser única y estable: es lo que
 * `Mueble.tipo` va a guardar en la planta, así que si dos piezas compartieran
 * clave la segunda repintaría a la primera.
 */
export function guardarPropio(
  ficha: Omit<ModeloMueble, 'clave' | 'propuesta'>,
): ModeloMueble {
  const nueva: ModeloMueble = { ...ficha, clave: `propio-${Date.now().toString(36)}` };
  escribirPropios([...leerPropios(), nueva]);
  return nueva;
}

export function borrarPropio(clave: string) {
  escribirPropios(leerPropios().filter((m) => m.clave !== clave));
}

// ───────────────────────────────── piezas apagadas ──

/**
 * Las piezas que Daniel sacó de circulación.
 *
 * Nace de un pedido suyo del 15-oct-2026: «no veo la opción para poder eliminar
 * un objeto, porque por ejemplo quiero borrar la ducha que creaste de 50 × 90
 * porque no existe, y lo que hace es siempre interpretar los clósets con esa
 * medida». Tenía razón dos veces: la opción no existía —la papelera estaba
 * negada a las de fábrica a propósito— y una pieza de fábrica que no existe en
 * los modelos de Adelante no es sólo ruido en la vitrina, **le roba bultos al
 * reconocedor**, que es el daño de verdad.
 *
 * Se APAGA, no se borra, y la diferencia importa:
 *
 *  - Una pieza de fábrica vive en el código. «Borrarla» desde la pantalla sería
 *    mentir: vuelve en el próximo despliegue.
 *  - Y los planos ya dibujados apuntan a su clave. Si desapareciera del índice,
 *    esas piezas perderían nombre y símbolo —el bug que documenta `PORCLAVE`—.
 *    Por eso `refrescarIndice` NO filtra apagadas: apagar saca la pieza de la
 *    vitrina y del reconocedor, nunca de un plano que ya la tiene puesta.
 *
 * Vive en ESTE navegador, igual que `leerPropios` y que las huellas. Mismo
 * precio y misma salida el día que haga falta: subirlo al servidor cambia de
 * dónde sale `leerApagadas`, nada más.
 */
const LLAVE_APAGADAS = 'planta.apagadas';

function leerApagadas(): string[] {
  try {
    const lista: unknown = JSON.parse(localStorage.getItem(LLAVE_APAGADAS) ?? '[]');
    return Array.isArray(lista) ? lista.filter((c): c is string => typeof c === 'string') : [];
  } catch { return []; }
}

/**
 * Cache de módulo, por lo mismo que `PORCLAVE`: `reconocer` pregunta una vez por
 * pieza de catálogo y por bulto del plano, y eso son miles de lecturas de
 * `localStorage` por plano abierto.
 */
let APAGADAS = new Set(leerApagadas());

function escribirApagadas(claves: string[]) {
  APAGADAS = new Set(claves);
  try {
    localStorage.setItem(LLAVE_APAGADAS, JSON.stringify(claves));
  } catch { /* ídem que en `escribirPropios`: se pierde el apagado, no el editor */ }
}

/** Si esta pieza está fuera de circulación. */
export const estaApagada = (clave: string) => APAGADAS.has(clave);

/** Saca la pieza de la vitrina y del reconocedor. Reversible. */
export function apagarPieza(clave: string) {
  if (!APAGADAS.has(clave)) escribirApagadas([...APAGADAS, clave]);
}

/** La devuelve a circulación. */
export function encenderPieza(clave: string) {
  escribirApagadas([...APAGADAS].filter((c) => c !== clave));
}

/** Cuántas hay apagadas, para que la pantalla pueda decirlo sin recorrer nada. */
export const cuantasApagadas = () => APAGADAS.size;

/**
 * Cambia una pieza propia sin moverla de lugar en la lista.
 *
 * Es el «mantenimiento» que pidió Daniel el 12-oct-2026: una pieza guardada con
 * la medida equivocada o con un nombre que después no se entiende no tenía más
 * arreglo que borrarla y volver a guardarla, y eso perdía el dibujo importado.
 *
 * La clave NO se toca aunque cambie todo lo demás: es lo que las piezas ya
 * puestas en el plano guardan en `Mueble.tipo`, así que cambiarla las dejaría
 * huérfanas —sin nombre y con el símbolo genérico— en planos ya terminados.
 */
export function editarPropio(clave: string, cambios: Partial<Omit<ModeloMueble, 'clave'>>) {
  escribirPropios(leerPropios().map((m) => (m.clave === clave ? { ...m, ...cambios } : m)));
}

/**
 * Renombra un grupo entero: reescribe `grupo` en todas MIS piezas que lo tienen.
 *
 * Daniel el 14-oct-2026: «no veo la opción de poder pasar un objeto que importé
 * a otro grupo, o de poder crear o editar otro grupo». Mover y crear ya existían
 * —escondidos, ver `SelectorGrupo`—; renombrar no existía.
 *
 * Devuelve cuántas piezas se movieron, que es lo que el aviso necesita contar.
 *
 * ⚠️ Sólo toca lo MÍO, y eso no es una limitación de implementación sino la
 * razón por la que `GRUPOS` no se puede renombrar: las piezas de fábrica viven
 * en el código y las del equipo en el servidor. Renombrar «Dormitorio» movería
 * mis cuatro piezas y dejaría las doce de fábrica donde estaban: el grupo
 * quedaría PARTIDO EN DOS con dos nombres. Por eso `grupoRenombrable()` es el
 * guardia, y no un `if` suelto en la pantalla.
 */
export function renombrarGrupo(viejo: string, nuevo: string): number {
  const destino = nuevo.trim();
  if (!destino || destino === viejo) return 0;
  let movidas = 0;
  escribirPropios(leerPropios().map((m) => {
    if (m.grupo !== viejo) return m;
    movidas += 1;
    return { ...m, grupo: destino };
  }));
  return movidas;
}

/**
 * Un grupo se puede renombrar sólo si NO hay piezas de fábrica ni del equipo
 * adentro — o sea, si es uno que nació de este dispositivo. Ver `renombrarGrupo`.
 */
export function grupoRenombrable(g: string): boolean {
  if (GRUPOS.includes(g)) return false;
  return !COMPARTIDOS.some((m) => m.grupo === g);
}

/**
 * Guarda una pieza con una clave QUE YA EXISTE, para corregir una de fábrica.
 *
 * Pedido de Daniel (12-oct-2026): «poder editar alguno de fábrica o existente…
 * el plato de ducha terminó siendo el símbolo de ducha pero con las dimensiones
 * de un plato». Hasta hoy una de fábrica no se podía tocar, y la vuelta larga
 * —bajar el dibujo, reimportarlo corregido— dejaba DOS piezas con el mismo
 * nombre y no arreglaba los planos que ya tenían puesta la vieja.
 *
 * La corrección conserva la clave a propósito: es lo que hace que los planos ya
 * dibujados se arreglen solos, porque `Mueble.tipo` sigue apuntando a la misma
 * pieza y `unir()` ahora deja que lo propio pise a lo de fábrica.
 *
 * No borra nada: la de fábrica sigue en `CATALOGO`, debajo. Por eso «volver al
 * de fábrica» es simplemente `borrarPropio(clave)`.
 */
export function guardarCorreccion(ficha: ModeloMueble): ModeloMueble {
  const otras = leerPropios().filter((m) => m.clave !== ficha.clave);
  escribirPropios([...otras, ficha]);
  return ficha;
}

/**
 * Si la pieza vive en este navegador, sea una nueva o la corrección de otra.
 *
 * Se pregunta a `leerPropios()` y NO al prefijo `propio-`, que es lo que hacía
 * antes: al corregir una pieza de fábrica la copia CONSERVA la clave de
 * fábrica —si no, los planos que ya la tienen puesta quedarían huérfanos—, así
 * que la prueba del prefijo la leía como «de fábrica» y la corrección se
 * mostraba como si no existiera.
 */
export const esPropio = (clave: string) => leerPropios().some((m) => m.clave === clave);

/**
 * Si existe una pieza de fábrica con esa clave.
 *
 * Es lo que distingue una corrección de un objeto nuevo: las dos son propias,
 * pero sólo la corrección tiene un original debajo al que se puede volver.
 */
export const esDeFabrica = (clave: string) => CATALOGO.some((m) => m.clave === clave);

/** El dibujo original de fábrica, para poder deshacer una corrección. */
export const fichaDeFabrica = (clave: string): ModeloMueble | undefined =>
  CATALOGO.find((m) => m.clave === clave);

/**
 * Las piezas que alguien publicó para TODO el equipo (`ObjetosPlanta` con
 * dueño nulo). Las trae el servidor; acá viven sólo mientras la página está
 * abierta, porque la fuente de verdad es la tabla y no este navegador.
 *
 * Quedan separadas de las propias en vez de mezclarse en `localStorage` para
 * que una pieza que el equipo despublica desaparezca en la siguiente carga:
 * si se guardaran acá, el catálogo compartido sería distinto en cada máquina
 * según cuándo la vio por última vez.
 */
let COMPARTIDOS: ModeloMueble[] = [];

export function fijarCompartidos(lista: ModeloMueble[]) {
  COMPARTIDOS = lista;

  // Publicar TRANSFIERE la pieza: deja de ser mía y pasa a ser del equipo. Pero
  // subirla no borraba la copia de este navegador, así que quedaban dos fichas
  // con la misma clave —una local vieja y la del servidor— y como lo propio
  // pisa a lo del equipo, la local ganaba PARA SIEMPRE.
  //
  // El modo de falla era mudo y desesperante: corregir la pieza guardaba bien en
  // el servidor, el aviso decía «quedó corregido para todo el equipo», y la
  // vitrina seguía mostrando la medida vieja. No había nada que tocar para
  // arreglarlo desde la pantalla.
  //
  // Se limpia acá y no al publicar a propósito: así también sanea los
  // navegadores que ya arrastran el residuo de una publicación anterior.
  const delEquipo = new Set(lista.map((m) => m.clave));
  const propios = leerPropios();
  if (propios.some((m) => delEquipo.has(m.clave))) {
    escribirPropios(propios.filter((m) => !delEquipo.has(m.clave)));
    return; // `escribirPropios` ya refresca el índice.
  }

  refrescarIndice();
}

export const leerCompartidos = (): ModeloMueble[] => COMPARTIDOS;

/**
 * Pega las listas en una sola sin repetir clave.
 *
 * Hace falta porque una pieza propia que además se publicó está en las DOS
 * listas —sigue siendo mía y ya es de todos— y sin esto saldría dos veces en
 * la vitrina.
 *
 * Reparte dos cosas distintas entre las listas, y ahí está toda la gracia:
 *
 *  - **El lugar** lo fija la PRIMERA aparición, así el orden de la vitrina no
 *    se baraja cuando alguien corrige una pieza de fábrica.
 *  - **El contenido** lo gana la ÚLTIMA, que es lo que permite pisar. Por eso
 *    se llama `unir(CATALOGO, COMPARTIDOS, leerPropios())`: lo mío pisa al
 *    equipo y el equipo pisa a fábrica.
 *
 * Antes ganaba la primera en las dos cosas, y por eso «editar uno de fábrica»
 * era imposible aunque el resto de la maquinaria ya estuviera lista: la copia
 * corregida existía en `localStorage` y no la veía nadie.
 */
function unir(...listas: ModeloMueble[][]): ModeloMueble[] {
  const orden: string[] = [];
  const porClave = new Map<string, ModeloMueble>();
  for (const lista of listas) {
    for (const m of lista) {
      if (!porClave.has(m.clave)) orden.push(m.clave);
      porClave.set(m.clave, m);
    }
  }
  return orden.map((c) => porClave.get(c)!);
}

/**
 * El índice por clave, que es de dónde salen el nombre y el símbolo de un mueble
 * ya puesto en el plano.
 *
 * Tiene que incluir las piezas propias o un mueble guardado se dibujaría como
 * el genérico y se llamaría «Mueble». Y se RECONSTRUYE al guardar o borrar
 * porque es un `Map` de módulo: si se armara una sola vez al cargar, la pieza
 * recién guardada no tendría nombre hasta recargar la página.
 */
let PORCLAVE = new Map<string, ModeloMueble>();
function refrescarIndice() {
  PORCLAVE = new Map(
    unir(CATALOGO, COMPARTIDOS, leerPropios()).map((m) => [m.clave, m]),
  );
}
refrescarIndice();

/** El catálogo completo que ve la vitrina: lo de fábrica, lo guardado y lo publicado. */
export const catalogoVivo = (): ModeloMueble[] =>
  unir(CATALOGO, COMPARTIDOS, leerPropios());

/** Los grupos a mostrar: los fijos más los que inventó Daniel al guardar. */
export function gruposVivos(): string[] {
  const extra = [...leerPropios(), ...COMPARTIDOS]
    .map((m) => m.grupo).filter((g) => !GRUPOS.includes(g));
  return [...GRUPOS, ...new Set(extra)];
}

export const fichaMueble = (clave: string): ModeloMueble | undefined => PORCLAVE.get(clave);

// ─────────────────────────── reconocer el mobiliario que ya trae el plano ──

/**
 * Un bulto de mobiliario tal como lo entrega el importador: su tamaño en
 * metros, de qué capa de Archicad salió y en qué cuarto cayó.
 *
 * El importador NO cruza contra el catálogo a propósito. De allá sale el
 * tamaño, la familia y el cuarto; la pieza la elige ACÁ, que es donde vive el
 * catálogo. Así el catálogo sigue teniendo un solo dueño y una pieza nueva
 * mejora el reconocimiento sin volver a medir un solo PDF.
 */
export interface Bulto {
  w: number;
  h: number;
  /** `mobiliario` o `sanitario`, según la capa del plano. */
  clase: string;
  /** El rótulo más cercano. Pista, no filtro: se equivoca en los pasillos. */
  cuarto?: string | null;
  /** La sigla celeste con que el plano NOMBRA el aparato. Ver `SIGLA_PIEZA`. */
  sigla?: string | null;
  /**
   * El lado, en metros, del anillo de desagüe que el importador encontró dentro
   * del bulto; `null` si no hay ninguno.
   *
   * Es la única señal que separa la ducha del inodoro, y no es por capricho:
   * medidos en el Burgos, la ducha del baño de abajo sale 0,64 × 0,38 y el
   * inodoro del MISMO baño 0,64 × 0,43. Cinco centímetros. Por tamaño no se
   * pueden separar y ningún catálogo más fino lo arregla. Lo que los separa es
   * físico: la ducha tiene desagüe.
   */
  desague?: number | null;
}

/**
 * Qué aparato nombra cada sigla celeste del plano.
 *
 * Archicad rotula los electrodomésticos con una letra de color
 * `rgb(67, 203, 254)` y el alfabeto es cerrado —medido sobre 40 croquis:
 * R, L, S, C, E, más «SC» de dos letras—. Daniel lo señaló antes que la
 * medición: «la secadora y la lavadora […] esa L en color celeste y SC».
 *
 * `E` queda a propósito SIN mapear. Aparece 23 veces y siempre empotrada en el
 * mostrador corrido de cocina, pero nadie dijo qué es; inventarle una pieza
 * sería poner un aparato que no se sabe si existe. Sin mapa, ese bulto sigue el
 * camino de siempre —el tamaño— y nada se rompe.
 */
const SIGLA_PIEZA: Record<string, string> = {
  L: 'lavadora', S: 'secadora', SC: 'secadora', R: 'refri', C: 'cocina',
};

/**
 * Cuánto puede errarle un lado, en metros, para que la pieza se acepte.
 *
 * 22 cm es generoso a propósito y no es descuido: el clóset del plano mide
 * 1,50 × 0,58 y el del catálogo 1,50 × 0,60; el inodoro sale 0,65 × 0,44 contra
 * 0,40 × 0,70. Apretarlo mandaría media casa al módulo «a medida», que es
 * justamente lo que Daniel no quiere («obliga a tener que irnos insertando cada
 * uno de los modelos»). Pasado el tope no se adivina: cae a «a medida» con su
 * tamaño real, que es honesto y es la pieza que su «reemplazar» corrige en un
 * toque.
 */
const TOL_LADO = 0.22;

/**
 * El mismo error, pero medido contra el tamaño de la pieza. Los 22 cm sueltos
 * son generosos para un clóset de 1,50 y ridículos para una rejilla de 0,30:
 * sin esto, un desagüe de 0,30 × 0,30 entraba como lavatorio de 0,50 × 0,35
 * —20 cm de error absoluto, pero el 40 % de la pieza— y el plano terminaba con
 * lavatorios que no existen. Hay que pasar los dos filtros.
 */
const TOL_PROPORCION = 0.35;

/** Qué grupos del catálogo puede ser cada capa del plano. */
const GRUPOS_CLASE: Record<string, string[]> = {
  // `MEC | Losa Sanitaria` es el inodoro, el lavatorio, la ducha, el fregadero
  // y la pila: loza, no muebles.
  sanitario: ['Baño', 'Pilas', 'Cocina'],
  mobiliario: GRUPOS,
};

/** Qué grupo sugiere el nombre del cuarto. Desempata, no manda. */
const GRUPO_CUARTO: Array<[RegExp, string]> = [
  [/ba[ñn]o|s\.?\s?s|w\.?\s?c/i, 'Baño'],
  [/dormitorio|cuarto|habitaci/i, 'Dormitorio'],
  [/cocina|pantry|desayun/i, 'Cocina'],
  [/pilas|lavado|lavander/i, 'Pilas'],
  [/sala|comedor|estar|oficina/i, 'Sala'],
];

/**
 * Qué piezas del catálogo es este bulto, dónde caen y para dónde miran.
 *
 * Devuelve una lista de UNO en el caso corriente. Son varias cuando el bloque
 * del plano dibuja varios muebles juntos —la cama con su mesa de noche— y eso
 * lo sabe únicamente la huella: ver `Huella.partes`. Nunca devuelve vacío; lo
 * que no calza con nada sale como un «a medida» del tamaño del bulto.
 *
 * Mide el error de las dos orientaciones y se queda con la mejor: el giro no es
 * un dato del plano —un rectángulo girado es otro rectángulo— se DEDUCE de cuál
 * de las dos calza, y es lo que deja la cama con el respaldo contra la pared y
 * el inodoro mirando al cuarto.
 *
 * Sólo mira el catálogo DE FÁBRICA, nunca las piezas guardadas: lo que el plano
 * trae tiene que leerse igual en el celular del papá que en la computadora, y
 * `leerPropios` es de ese navegador (ver `LLAVE_PROPIOS`).
 */
export function reconocer(b: Bulto): PiezaBulto[] {
  const permitidos = GRUPOS_CLASE[b.clase] ?? GRUPOS;
  const sugerido = GRUPO_CUARTO.find(([re]) => re.test(b.cuarto ?? ''))?.[1];
  // La sigla del plano es la mejor pista que hay —el arquitecto escribió qué
  // es— pero NO salta los filtros de tamaño: el mostrador corrido de cocina
  // lleva la R adentro y dibujarlo como refrigeradora daría una nevera de dos
  // metros. Si la pieza nombrada no calza, el bulto cae a «a medida», que es la
  // respuesta honesta.
  const nombrada = SIGLA_PIEZA[(b.sigla ?? '').toUpperCase()];
  // La HUELLA APRENDIDA se consulta antes que el puntaje, y es lo único que se
  // consulta antes. Ver `piezaDeHuella`: no compite por centímetros, reconoce un
  // bloque que el dibujante reutiliza. Sólo la sigla le gana, porque la sigla es
  // el arquitecto diciendo por escrito qué aparato es.
  if (!nombrada) {
    const sabidas = piezasDeHuella(b, permitidos);
    if (sabidas) return sabidas;
  }
  let mejor: { tipo: string; giro: 0 | 90 } | null = null;
  let menor = Infinity;
  for (const m of CATALOGO) {
    // Una pieza APAGADA no compite, ni siquiera nombrada por la sigla. Es el
    // punto del pedido: si la ducha de 50 × 90 no existe en los modelos de
    // Adelante, lo que mide 50 × 90 en el plano es otra cosa —un clóset— y el
    // reconocedor tiene que poder llegar a esa otra cosa. Ver `LLAVE_APAGADAS`.
    if (estaApagada(m.clave)) continue;
    // La sigla pisa el filtro de la capa: que el plano haya puesto la lavadora
    // en una capa sanitaria no la vuelve un inodoro.
    if (m.flexible || (!permitidos.includes(m.grupo) && nombrada !== m.clave)) continue;
    // El ELÉCTRICO nunca se reconoce del plano. No es una limitación: el croquis
    // arquitectónico no dibuja salidas eléctricas, así que cualquier coincidencia
    // sería un falso positivo —y como el símbolo mide 20 cm y la tolerancia de
    // lado es generosa, cualquier manchita de 20 cm del dibujo se volvería un
    // tomacorriente—. Estas piezas las pone una persona, a mano.
    if (m.electrico) continue;
    // Y la que sólo llega por huella tampoco compite por centímetros: ver
    // `soloHuella`. La mesa de noche se comía las sillas.
    if (m.soloHuella) continue;
    // Una pieza que vive de su desagüe no se pone en un bulto que no lo trae.
    // Es lo que impide que el plato de ducha se le monte al inodoro de al lado,
    // que mide casi lo mismo.
    if (m.desague && !b.desague) continue;
    for (const giro of [0, 90] as const) {
      const [mw, mh] = giro === 0 ? [m.w, m.h] : [m.h, m.w];
      const e = Math.max(Math.abs(b.w - mw), Math.abs(b.h - mh));
      if (e > TOL_LADO) continue;
      const prop = Math.max(Math.abs(b.w - mw) / mw, Math.abs(b.h - mh) / mh);
      if (prop > TOL_PROPORCION) continue;
      // La pista del cuarto vale un castigo chico: alcanza para que una ducha
      // de 0,90 en un baño le gane a una refrigeradora de 0,90, y no alcanza
      // para imponer una pieza que no calza de tamaño.
      const con = e + (sugerido && m.grupo !== sugerido ? 0.06 : 0)
                    - (nombrada === m.clave ? 1 : 0);
      if (con < menor) { menor = con; mejor = { tipo: m.clave, giro }; }
    }
  }
  // El puntaje nunca parte un bulto: lo que elige ocupa el bloque entero.
  const { tipo, giro } = mejor ?? { tipo: 'medida', giro: 0 as const };
  return [{ tipo, giro, x: 0, y: 0, w: b.w, h: b.h }];
}

export const nombreMueble = (m: Mueble): string => PORCLAVE.get(m.tipo)?.nombre ?? 'Mueble';

/** Si este mueble conserva los agarres sobre el plano. Ver `ModeloMueble.flexible`. */
export const muebleFlexible = (m: Mueble): boolean => PORCLAVE.get(m.tipo)?.flexible === true;

/**
 * La pieza comodín del catálogo: la ÚNICA que no trae medida propia.
 *
 * No es lo mismo que `flexible`. Una pieza GUARDADA que salió de «A medida»
 * también hereda `flexible` —conserva los agarres— pero sí tiene medida propia:
 * es justo la que Daniel moldeó y nombró. Por eso al reemplazar hay que
 * distinguirlas: «A medida» conserva la huella del origen, una pieza guardada
 * trae la suya.
 */
export const CLAVE_A_MEDIDA = 'medida';

// ──────────────────────────── el inventario de huellas aprendidas ──

/**
 * Cuánto pueden diferir dos medidas para ser el MISMO bloque de Archicad.
 *
 * 3 cm, mucho más apretado que `TOL_LADO` y a propósito: esto NO adivina
 * parecidos, reconoce un bloque que el dibujante REUTILIZA. Medido sobre los
 * 1.095 objetos de los 50 modelos publicados, esos bloques caen en números
 * redondos —1,20 × 2,00 sale 22 veces y 1,90 × 2,20 sale 21, al centímetro— así
 * que lo único que hay que tolerar es el redondeo del PDF, no una familia de
 * tamaños. Es la medición que respalda lo que Daniel dijo el 14-oct-2026: «al
 * final es un inventario de máximo 20 objetos que debería aprender a reconocer».
 */
const TOL_HUELLA = 0.03;

/**
 * Qué pieza es una huella. `s` es el lado corto y `l` el largo, SIN orientación:
 * un bloque acostado es el mismo bloque, y el giro se deduce después contra la
 * medida de la pieza.
 */
export interface Huella {
  s: number;
  l: number;
  /**
   * La pieza PRINCIPAL del bloque. Es la que se enseña con un toque y la que
   * `huellaDe(...).clave` responde, así que sigue siendo una clave sola aunque
   * el bloque traiga más piezas.
   */
  clave: string;
  /**
   * Las piezas que el bloque dibuja JUNTAS, cuando es más de una.
   *
   * Nace de un plano de Daniel: el croquis del Burgos T dibuja la cama y su
   * mesa de noche en un solo objeto de Archicad, y el bulto sale 1,30 × 1,90.
   * Medido contra el catálogo, la individual (1,00) erraba 30 cm y la
   * matrimonial (1,35) erraba 5, así que una cama individual se venía
   * dibujando matrimonial —y a veces queen— por culpa de la mesa de noche.
   * Partir el bulto es lo único que lo arregla: medir mejor no sirve cuando lo
   * medido son dos muebles.
   *
   * Las medidas van EN METROS dentro del bulto puesto de pie —lado corto sobre
   * `x`, lado largo sobre `y`— y `piezasDeHuella` las gira cuando el bulto
   * viene acostado. Sin `partes`, la huella enseña una sola pieza y ocupa el
   * bulto entero, que es como funcionaba antes.
   */
  partes?: ParteHuella[];
}

/** Una pieza dentro de un bloque compuesto, medida en metros desde su esquina. */
export interface ParteHuella { clave: string; x: number; y: number; w: number; h: number }

/** Una pieza ya resuelta contra un bulto: qué es, cómo va girada y dónde cae. */
export interface PiezaBulto {
  tipo: string;
  giro: 0 | 90;
  /** Desplazamiento en metros desde la esquina del bulto. */
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Dos huellas que nombran el mismo bloque. */
const mismaHuella = (a: { s: number; l: number }, b: { s: number; l: number }) =>
  Math.abs(a.s - b.s) <= TOL_HUELLA && Math.abs(a.l - b.l) <= TOL_HUELLA;

/**
 * Las huellas que vienen medidas de fábrica.
 *
 * Sólo entran las que el propio catálogo PRUEBA: la misma huella resuelta como
 * cama dentro de un dormitorio rotulado, y como mueble de sala donde el rótulo
 * falta o miente. 1,90 × 2,20 son 21 objetos —7 salen cama king en un
 * dormitorio rotulado y 14 sofá en L por no tener rótulo— y 1,20 × 1,90 son 10,
 * con 2 y 8.
 *
 * 1,90 × 2,40 ENTRÓ el 15-oct-2026 y vale la pena decir por qué, porque este
 * mismo comentario decía antes que no podía entrar. La prueba no salió de los
 * 50 modelos publicados —ahí el bloque no se resuelve bien en ninguno, que es
 * lo que decía la nota vieja— sino de medir los 183 croquis del blob: 74 bultos,
 * 68 al centímetro exacto, y el rótulo del recinto los ubica solos (39 en
 * «Dormitorio Princ.», 12 en «Dormitorio I», 12 en «Dormitorio», 4 en
 * «Dormitorio Principal»). **Ni uno en sala ni en terraza**, que es la única
 * razón por la que se puede escribir de fábrica sin robarle bultos a un sofá.
 *
 * La que sigue afuera es 1,20 × 2,00 (22 veces): esa no la prueba nada todavía.
 * La enseña Daniel con un toque, que es para lo que existe el resto del bloque.
 */
const HUELLAS_FABRICA: Huella[] = [
  { s: 1.90, l: 2.20, clave: 'cama-king' },
  { s: 1.20, l: 1.90, clave: 'cama-matrimonial' },
  // Cama individual + mesa de noche en un solo bloque. La trajo Daniel con el
  // plano del Burgos T en la mano: «realmente esto es una cama individual, por
  // eso es que siempre termina poniendo esa matrimonial o queen». Los 1,30 de
  // ancho son 1,00 de cama y 0,30 de mesa, y el largo de 1,90 ya era correcto.
  // Con los 3 cm de `TOL_HUELLA` esta sola entrada cubre los dos números que
  // el catálogo repite —1,30 × 1,90 (16 bultos) y 1,31 × 1,90 (18)—: 34 camas
  // que hasta hoy salían de un tamaño que nadie compró.
  {
    s: 1.30,
    l: 1.90,
    clave: 'cama-individual',
    partes: [
      { clave: 'cama-individual', x: 0, y: 0, w: 1.00, h: 1.90 },
      // A la cabecera, que es donde la dibuja el plano. De qué lado queda la
      // mesa no se puede saber del bulto solo —es un rectángulo— así que se
      // elige uno; moverla es arrastrarla.
      { clave: 'mesa-noche', x: 1.00, y: 0, w: 0.30, h: 0.40 },
    ],
  },
  // Cama queen + DOS mesas de noche, el bloque del dormitorio principal. Lo
  // pidió Daniel el 15-oct-2026 y la descomposición cierra exacta contra el
  // catálogo que ya existe, sin inventar ninguna pieza:
  //
  //     0,40 (mesa) + 1,60 (queen) + 0,40 (mesa) = 2,40
  //
  // Hasta hoy esos 74 bultos salían `sofa-l`, y salían sofá en L INCLUSO con el
  // recinto rotulado «Dormitorio Princ.»: el rótulo no alcanza a salvarlos
  // porque la queen sola (1,60 × 1,90) sí se reconoce bien — lo que no existía
  // era el bloque. Acá el lado CORTO es el largo de la cama: el bloque crece a
  // lo ancho, no a lo largo, y por eso `s` es 1,90 y `l` 2,40.
  {
    s: 1.90,
    l: 2.40,
    clave: 'cama-queen',
    partes: [
      // Las dos mesas van a la cabecera, en x = 0, una a cada lado. De qué lado
      // del cuarto cae la cabecera no se puede saber de un rectángulo —mismo
      // razonamiento que en la individual—, así que se elige un extremo y
      // moverlas es arrastrarlas.
      { clave: 'mesa-noche', x: 0, y: 0, w: 0.40, h: 0.40 },
      // La cama ocupa TODO el largo del bloque y el ancho de en medio. Va
      // acostada respecto de su ficha (1,60 × 1,90), y `giroDe` lo resuelve
      // solo a partir de estas medidas.
      { clave: 'cama-queen', x: 0, y: 0.40, w: 1.90, h: 1.60 },
      { clave: 'mesa-noche', x: 0, y: 2.00, w: 0.40, h: 0.40 },
    ],
  },
];

/**
 * Lo aprendido vive en ESTE navegador, igual que `leerPropios`.
 *
 * Es una limitación real y hay que decirla: una huella enseñada en la compu no
 * viaja al celular. Se asume a cambio de que enseñar sea un toque y funcione
 * hoy; el día que las huellas vivan en el servidor, lo único que cambia es de
 * dónde sale `huellasVivas` —el resto del reconocedor no se entera—.
 */
const LLAVE_HUELLAS = 'planta.huellas';

function leerHuellas(): Huella[] {
  try {
    const crudo: unknown = JSON.parse(localStorage.getItem(LLAVE_HUELLAS) ?? '[]');
    if (!Array.isArray(crudo)) return [];
    // Se verifica la forma y no se confía en lo guardado: lo escribió una
    // versión anterior de este mismo código y una huella sin número mandaría a
    // `NaN` toda comparación, que es la forma silenciosa de aprobarlo todo.
    return crudo.filter((h): h is Huella =>
      typeof h?.s === 'number' && typeof h?.l === 'number' && typeof h?.clave === 'string');
  } catch { return []; }
}

/** Lo aprendido pisa a lo de fábrica, bloque por bloque. */
export const huellasVivas = (): Huella[] => {
  const mias = leerHuellas();
  return [...mias, ...HUELLAS_FABRICA.filter((f) => !mias.some((m) => mismaHuella(m, f)))];
};

/** Qué pieza es esta medida, si alguien ya lo enseñó. */
export const huellaDe = (w: number, h: number): Huella | undefined =>
  huellasVivas().find((q) => mismaHuella(q, { s: Math.min(w, h), l: Math.max(w, h) }));

/**
 * Enseña que esta medida es esta pieza. Devuelve el motivo si no se puede, o
 * `null` si quedó aprendida.
 *
 * Dos piezas no se dejan enseñar, y las dos por la misma razón —no son una
 * pieza, son el resultado de no haber reconocido ninguna—:
 *
 *  - **«A medida»**, que es literalmente lo que queda cuando nada calza.
 *  - **El eléctrico**, porque el croquis arquitectónico no dibuja salidas
 *    eléctricas: aprender una huella eléctrica volvería tomacorriente a cada
 *    manchita de 20 cm del dibujo. Ver el filtro de `reconocer`.
 */
export function aprenderHuella(w: number, h: number, clave: string): string | null {
  if (clave === CLAVE_A_MEDIDA) {
    return '«A medida» no es una pieza: es lo que queda cuando ninguna calza.';
  }
  const m = PORCLAVE.get(clave);
  if (!m) return 'Esa pieza no está en el catálogo.';
  if (estaApagada(clave)) return 'Esa pieza está apagada: encendela primero en Objetos.';
  if (m.electrico) return 'El croquis no dibuja salidas eléctricas: ésas las pone una persona.';
  const nueva: Huella = { s: +Math.min(w, h).toFixed(3), l: +Math.max(w, h).toFixed(3), clave };
  const resto = leerHuellas().filter((q) => !mismaHuella(q, nueva));
  try {
    localStorage.setItem(LLAVE_HUELLAS, JSON.stringify([...resto, nueva]));
  } catch { return 'No se pudo guardar en este navegador.'; }
  return null;
}

/** Desaprende esta medida. Vuelve a decidirla el puntaje, o la huella de fábrica. */
export function olvidarHuella(w: number, h: number): void {
  const q = { s: Math.min(w, h), l: Math.max(w, h) };
  try {
    localStorage.setItem(LLAVE_HUELLAS,
      JSON.stringify(leerHuellas().filter((x) => !mismaHuella(x, q))));
  } catch { /* sin almacenamiento no hay nada aprendido que borrar */ }
}

/** De qué lado mira la pieza: la orientación del catálogo que más se le parece. */
const giroDe = (m: ModeloMueble, w: number, h: number): 0 | 90 =>
  (Math.abs(w - m.h) + Math.abs(h - m.w) < Math.abs(w - m.w) + Math.abs(h - m.h) ? 90 : 0);

/**
 * La pieza del catálogo que vale para este bulto, o `null` si no pasa los
 * filtros DUROS.
 *
 * **Los dos filtros duros del reconocedor siguen mandando aunque la huella ya
 * haya dicho qué es.** La huella enseña qué pieza es, no en qué capa vive: sin
 * el filtro de capa, enseñarle que 0,90 × 0,90 es una refrigeradora
 * convertiría en nevera todas las duchas del catálogo, que miden exactamente
 * lo mismo. Y sin el del desagüe, el plato de ducha volvería a montarse sobre
 * el inodoro de al lado.
 */
function fichaValida(clave: string, b: Bulto, permitidos: string[]): ModeloMueble | null {
  const m = PORCLAVE.get(clave);
  if (!m || m.electrico) return null;
  // Apagada tampoco llega por huella. Es el mismo filtro que en el puntaje y va
  // acá porque éste es el punto único por donde pasan TODAS las partes de una
  // huella compuesta: una cama cuya mesa de noche está apagada cae entera al
  // puntaje, que es la regla de «todo o nada» ya documentada en `piezasDeHuella`.
  if (estaApagada(clave)) return null;
  if (!permitidos.includes(m.grupo)) return null;
  if (m.desague && !b.desague) return null;
  return m;
}

/**
 * Las piezas que dice la huella aprendida, o `null` si nadie enseñó ésta.
 *
 * Devuelve una LISTA porque un bloque del plano puede ser más de un mueble:
 * ver `Huella.partes`. Sin partes es una pieza sola que ocupa el bulto entero,
 * y el giro sale como en el resto del reconocedor —cuál de las dos
 * orientaciones de la pieza se parece más a lo que trae el plano—, que es lo
 * que deja la cama con el respaldo contra la pared.
 *
 * **Una huella compuesta es todo o nada.** Si una de sus partes no pasa los
 * filtros duros, se descarta la huella entera y el bulto cae al puntaje: media
 * cama dibujada es peor que una cama del tamaño equivocado.
 */
function piezasDeHuella(b: Bulto, permitidos: string[]): PiezaBulto[] | null {
  const hu = huellaDe(b.w, b.h);
  if (!hu) return null;
  if (!hu.partes?.length) {
    const m = fichaValida(hu.clave, b, permitidos);
    return m ? [{ tipo: m.clave, giro: giroDe(m, b.w, b.h), x: 0, y: 0, w: b.w, h: b.h }] : null;
  }
  // Las partes se guardan con el bloque DE PIE (lado corto en x). Cuando el
  // plano lo trae acostado hay que girarlas con él, o la mesa de noche
  // aparecería fuera del bulto.
  const acostado = b.w > b.h;
  const largo = Math.max(b.w, b.h);
  const piezas: PiezaBulto[] = [];
  for (const p of hu.partes) {
    const m = fichaValida(p.clave, b, permitidos);
    if (!m) return null;
    const [x, y, w, h] = acostado
      ? [largo - p.y - p.h, p.x, p.h, p.w]
      : [p.x, p.y, p.w, p.h];
    piezas.push({ tipo: m.clave, giro: giroDe(m, w, h), x, y, w, h });
  }
  return piezas;
}

/** Las esquinas en el orden de `Mueble.radios`: el del reloj desde arriba-izquierda. */
export const ESQUINAS = ['no', 'ne', 'se', 'so'] as const;
export type Esquina = (typeof ESQUINAS)[number];

/**
 * El índice en `Mueble.radios` que corresponde a la esquina que se tocó EN EL
 * PLANO, deshaciendo el giro. `-1` si el lado tocado no es una esquina.
 *
 * Hace falta porque los radios viven en coordenadas locales del símbolo —giran
 * con el dibujo— y los agarres están en las del plano: en un mueble girado 90°
 * la esquina de arriba a la derecha del plano ES la de arriba a la izquierda
 * del dibujo. Sin esta vuelta, redondear una esquina redondearía otra.
 */
export function iEsquina(lado: string, giro = 0): number {
  const i = (ESQUINAS as readonly string[]).indexOf(lado);
  if (i < 0) return -1;
  return (i - giro / 90 + 4) % 4;
}

/**
 * Pinta el símbolo de un mueble ya puesto, en coordenadas del plano.
 *
 * El giro se resuelve con UN transform y por eso las medidas propias —las de
 * antes de girar— se recuperan intercambiando: un clóset de 1,50 × 0,60 girado
 * 90° ocupa 0,60 × 1,50 en el plano, pero su dibujo sigue siendo el de
 * 1,50 × 0,60 puesto de costado. Dibujarlo con las medidas ya intercambiadas
 * daría una cama cuyas almohadas ocupan el lado largo.
 *
 * `tinta` pisa el color de todo el símbolo. Los trazos de acá abajo están
 * escritos en `currentColor` justamente para eso: Daniel pidió que el estado de
 * rehabilitación cambie «las líneas del SVG, no que le haga un recuadro». Sin
 * `tinta`, cada pieza vuelve a su color de familia —gris el mueble, marrón la
 * salida eléctrica—, que es lo que dice QUÉ es.
 *
 * El espejo va DENTRO del giro y antes de centrar, así que voltea la pieza
 * sobre su propio eje sin moverla del lugar: un sofá en L girado 90° y
 * espejado sigue ocupando la misma caja.
 */
/**
 * Pinta un trazo importado dentro de la medida de la pieza.
 *
 * El dibujo llegó con sus propias unidades —puntos de un PDF, píxeles de un
 * SVG— y acá se estira hasta llenar `w × h`. Se estira por EJE y no por un
 * factor único a propósito: el usuario dictó las dos medidas de su pieza, y
 * respetar la proporción del archivo dejaría aire a los lados de algo que él
 * dijo que mide 1,50 × 0,60.
 *
 * El grosor se divide por el estiramiento promedio para que termine valiendo lo
 * mismo que `TRAZO` sobre el plano. La alternativa —`non-scaling-stroke`— deja
 * la línea clavada en píxeles de pantalla y entonces el objeto importado sería
 * el único que no engorda al acercar el zoom.
 */
function PintaTrazo({ t, w, h }: { t: Trazo; w: number; h: number }) {
  const [cx, cy, cw, ch] = t.caja;
  const sx = w / (cw || 1);
  const sy = h / (ch || 1);
  const grosor = TRAZO / Math.max((Math.abs(sx) + Math.abs(sy)) / 2, 1e-6);
  return (
    <g transform={`scale(${sx} ${sy}) translate(${-cx} ${-cy})`}>
      {t.d.map((d, i) => (
        <path key={i} d={d} fill="none" stroke="currentColor"
              strokeWidth={grosor} strokeLinecap="round" strokeLinejoin="round" />
      ))}
    </g>
  );
}

/**
 * El dibujo de una ficha: el suyo propio si lo trae, y si no el del mapa.
 *
 * Se exporta para que `exportar-simbolo` baje EXACTAMENTE lo que se ve en la
 * vitrina y en el plano. Si la descarga tuviera su propia versión del dibujo,
 * las dos se separarían al primer símbolo que alguien retoque.
 */
export function dibujoDe(ficha: ModeloMueble | undefined, w: number, h: number, m?: Mueble) {
  if (ficha?.trazo) return <PintaTrazo t={ficha.trazo} w={w} h={h} />;
  return (SIMBOLOS[ficha?.simbolo ?? ''] ?? SIMBOLOS.mueble)(w, h, m);
}

export function SimboloMueble({ m, tinta }: { m: Mueble; tinta?: string }) {
  const ficha = PORCLAVE.get(m.tipo);
  // CON FORMA PROPIA el objeto se pinta tal cual, en coordenadas de mundo, sin
  // pasar por el transform de giro/espejo: esos existen para orientar un dibujo
  // FIJO, y acá el dibujo es la forma. Ver `Mueble.vertices`.
  if (m.vertices?.length) {
    return (
      <polygon
        points={m.vertices.map(([x, y]) => `${x},${y}`).join(' ')}
        fill="#ffffff" fillOpacity={0.5}
        stroke={tinta ?? (ficha?.electrico ? TINTA_ELECTRICO : TINTA_MUEBLE)}
        strokeWidth={TRAZO} strokeLinejoin="round"
      />
    );
  }
  const giro = m.giro ?? 0;
  const vertical = giro === 90 || giro === 270;
  const pw = vertical ? m.h : m.w;
  const ph = vertical ? m.w : m.h;
  return (
    <g transform={`translate(${m.x + m.w / 2} ${m.y + m.h / 2}) rotate(${giro})`
                  + (m.espejo ? ' scale(-1 1)' : '')
                  + ` translate(${-pw / 2} ${-ph / 2})`}
       style={{ color: tinta ?? (ficha?.electrico ? TINTA_ELECTRICO : TINTA_MUEBLE) }}>
      {dibujoDe(ficha, pw, ph, m)}
    </g>
  );
}

/**
 * La miniatura del catálogo: el MISMO símbolo, encuadrado en su medida.
 *
 * Es el mismo dibujo a propósito —no un ícono parecido—: lo que se elige con el
 * dedo es exactamente lo que va a caer en el plano.
 */
export function MiniaturaMueble({ modelo, px = 34 }: { modelo: ModeloMueble; px?: number }) {
  // Un poco de aire: los trazos tienen grosor y a ras del borde se recortan.
  const aire = 0.05;
  const lado = Math.max(modelo.w, modelo.h) + aire * 2;
  return (
    <svg width={px} height={px}
         viewBox={`${(modelo.w - lado) / 2} ${(modelo.h - lado) / 2} ${lado} ${lado}`}
         style={{ color: modelo.electrico ? TINTA_ELECTRICO : TINTA_MUEBLE }}
         aria-hidden="true">
      {dibujoDe(modelo, modelo.w, modelo.h)}
    </svg>
  );
}

/**
 * Las coordenadas de un eje a las que un MUEBLE se arrima.
 *
 * Es la lista que `nodos` no podía dar, y la diferencia no es un detalle: `nodos`
 * devuelve el EJE de cada pared, que es la convención correcta para dibujar
 * paredes —en estas plantas las fachadas se cruzan sobre sus ejes—, pero un
 * mueble no se arrima a un eje: se arrima a la CARA. Un clóset pegado al eje de
 * una pared de 12 cm queda 6 cm metido dentro del muro.
 *
 * Por eso van las dos caras (`c ± e/2`) y además las puntas de las
 * perpendiculares, que son las esquinas —lo que Daniel pidió como «que reconozca
 * los bordes o esquinas de las paredes»—.
 *
 * Y van los cuatro bordes de cada mueble ya puesto, que es el apilado: «los
 * muebles de cocina, que uno pueda ir apilando y vaya reconociendo los bordes».
 * Sale del mismo mecanismo porque un borde de mueble y una cara de pared son la
 * misma cosa para quien arrima: una línea contra la que topar.
 */
export function nodosMueble(
  ws: Pared[], ms: Mueble[], eje: 'x' | 'y', salvo: number,
): number[] {
  const out: number[] = [];
  for (const w of ws) {
    if (w.o === (eje === 'x' ? 'v' : 'h')) out.push(w.c - w.e / 2, w.c + w.e / 2);
    else out.push(w.a, w.b);
  }
  for (const m of ms) {
    if (m.id === salvo) continue;
    if (eje === 'x') out.push(m.x, m.x + m.w);
    else out.push(m.y, m.y + m.h);
  }
  return out;
}

/**
 * Dónde dejar el borde inicial de una caja de largo `largo` para que ALGUNO de
 * sus dos bordes quede clavado en un candidato. Null si no hay ninguno a tiro.
 *
 * Probar los dos bordes es lo que hace que apilar funcione en las dos
 * direcciones: al traer un módulo desde la izquierda lo que topa es su borde
 * derecho, y al traerlo desde la derecha, su izquierdo. Con un solo borde el
 * imán agarraría la mitad de las veces y se leería como que falla.
 */
export function arrimar(
  v0: number, largo: number, cands: number[], umbral: number,
): number | null {
  let mejor: number | null = null;
  let d = umbral;
  for (const c of cands) {
    for (const borde of [0, largo]) {
      const k = Math.abs(c - borde - v0);
      if (k < d) { d = k; mejor = c - borde; }
    }
  }
  return mejor;
}
